import { config } from "@config/config";
import { ArrowsPointingInIcon, ArrowsPointingOutIcon, Cog6ToothIcon } from "@heroicons/react/24/outline";
import { downloadSampleTemplatePdfFile } from "@utils/utils";
import { isEqual } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import { Button, PopoverPanel, SkeletonLoader, Small } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportTemplateReportTypeChoices,
  ApiReportTemplateSampleStatusChoices,
  ApiReportTemplateStatusChoices,
  BaseFieldType,
  ReportTemplateSampleStatusEnums,
  ReportTemplateSectionInput,
  ReportTemplateStatus,
  ReportTemplateType,
  TreatmentPlanTemplateFieldInput,
  TreatmentPlanTemplateFieldTypeEnum,
  UpdateReportTemplateInput,
  useCreateSampleTemplateMutation,
  useReportTemplateGlobalsQuery,
  useReportTemplateQuery,
  useReportTemplateSampleStatusQuery,
  useUpdateReportTemplateMutation,
} from "@api/graphql/types-and-hooks";
import { SegmentedTabs } from "@components/SegmentedTabs/SegmentedTabs";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { StickyNavCollapsible } from "@pages/Templates/components/StickyNav/StickyNavCollapsible";
import { TemplateEditorFooterButtonRow } from "@pages/Templates/components/TemplateEditorFooterButtonRow";
import { TemplateExportActionPanel } from "@pages/Templates/components/TemplateExportActionPanel/TemplateExportActionPanel";
import { TemplateExportJson } from "@pages/Templates/components/TemplateExportJSON/TemplateExportJSON";
import { TemplateHeader } from "@pages/Templates/components/TemplateHeader/TemplateHeader";
import { TemplateSettings } from "@pages/Templates/components/TemplateSettings/TemplateSettings";
import { TemplateStructure } from "@pages/Templates/components/TemplateStructure/TemplateStructure";
import {
  TemplateFieldState,
  TreatmentPlanTemplateState,
} from "@pages/Templates/components/TreatmentPlanTemplateEditor/TreatmentPlanTemplateEditor";
import {
  SavedIds,
  buildTemplateSections,
  matchSavedIds,
  rebaseOnServer,
  resolveItemId,
  sendsTemporaryIds,
  toSectionInputs,
} from "@pages/Templates/utils";
import { useAdminData } from "@providers/AdminDataProvider";
import { ReportTemplateSection, TemplateEditorProvider } from "@providers/TemplateEditorProvider";

const RELOAD_AFTER_SAVE_MESSAGE =
  "Your template was saved, but the saved version doesn't match the editor. Reload the page to see what was saved before making more changes.";

// A save that sent no temporary ids has nothing to pair.
const NO_SAVED_IDS: SavedIds = { sections: new Map(), fields: new Map() };

function mapFieldToInput(f: TemplateFieldState): TreatmentPlanTemplateFieldInput {
  return {
    id: f.id.startsWith("temp-") ? undefined : f.id,
    title: f.title,
    fieldType: f.fieldType as BaseFieldType,
    rankOrder: f.rankOrder,
    isRequired: f.isRequired,
    isEnabled: f.isEnabled,
    content: f.content,
    type: f.type as TreatmentPlanTemplateFieldTypeEnum,
    reportGoalField: f.reportGoalField,
    reportTargetField: f.reportTargetField,
  };
}

function buildSaveInput(
  template: ReportTemplateType,
  sections: ReportTemplateSectionInput[],
  treatmentPlan: TreatmentPlanTemplateState | null,
): UpdateReportTemplateInput {
  return {
    reportTemplateId: template.id || "",
    name: template.name,
    description: template.description,
    status: template.status as unknown as ReportTemplateStatus,
    reportTemplateSections: sections,
    ...(treatmentPlan
      ? {
          treatmentPlanTemplate: {
            defaultFields: treatmentPlan.defaultFields.map(mapFieldToInput),
            domains: treatmentPlan.domains.map((d) => ({
              id: d.id.startsWith("temp-") ? undefined : d.id,
              name: d.name,
              description: d.description,
              rankOrder: d.rankOrder,
              templateFields: d.templateFields.map(mapFieldToInput),
            })),
          },
        }
      : {}),
  };
}

export const TEMPLATE_EDITOR_TABS = {
  NARRATIVE: "narrative",
  PROGRAMMING: "programming",
} as const;

export type TemplateEditorTab = (typeof TEMPLATE_EDITOR_TABS)[keyof typeof TEMPLATE_EDITOR_TABS];

export const TemplateEditor: React.FC = () => {
  const [changed, setChanged] = useState(false);
  const [collapseFields, setCollapseFields] = useState({ value: true });
  const [reportTemplate, setReportTemplate] = useState<ReportTemplateType>();
  // The only copy of the sections; TemplateStructure renders and edits it through replaceSections.
  const [reportTemplateSections, setReportTemplateSections] = useState<ReportTemplateSection[]>([]);
  const sectionsRef = useRef<ReportTemplateSection[]>([]);
  // The last server-built copy taken whole; the current copy still being it means no unsaved edits.
  const adoptedSectionsRef = useRef<ReportTemplateSection[] | null>(null);
  // Saved id -> the temporary id the item was added with, so its React key and collapse state survive a save.
  const [itemKeys, setItemKeys] = useState<Record<string, string>>({});
  const itemKeysRef = useRef<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);
  const [reloadRequired, setReloadRequired] = useState(false);
  const [treatmentPlanTemplateState, setTreatmentPlanTemplateState] = useState<TreatmentPlanTemplateState | null>(null);
  const [sampleInvalidated, setSampleInvalidated] = useState(false);
  const [activeTab, setActiveTab] = useState<TemplateEditorTab>(TEMPLATE_EDITOR_TABS.NARRATIVE);

  // Each ref is set with its state so a handler running before the re-render reads the new value.
  const replaceSections = (sections: ReportTemplateSection[]) => {
    sectionsRef.current = sections;
    setReportTemplateSections(sections);
  };
  const replaceItemKeys = (keys: Record<string, string>) => {
    itemKeysRef.current = keys;
    setItemKeys(keys);
  };
  const markSaving = (saving: boolean) => {
    isSavingRef.current = saving;
    setIsSaving(saving);
  };
  // TemplateStructure's handlers read these, not render-time props: a save can swap ids before the
  // next render, and a drag or dialog can hold an id from before the swap.
  const getLatestSections = () => sectionsRef.current;
  const resolveSectionItemId = (id: string) => resolveItemId(id, sectionsRef.current, itemKeysRef.current);

  const { reportTemplateId: reportTemplateIdParam } = useParams();
  const { pathname } = useLocation();

  const handleSaveTitle = async (newTitle: string) => {
    if (!reportTemplate) return;

    try {
      await updateReportTemplate.mutateAsync({
        input: {
          reportTemplateId: reportTemplate.id || "",
          name: newTitle,
        },
      });

      setReportTemplate((prev) => (prev ? { ...prev, name: newTitle } : prev));
    } catch (error) {
      console.error("Error saving title:", error);
      notifyError("Could not save title.");
    }
  };

  const { isUserEnterpriseAdmin } = useAdminData();

  // decode base64 encoded id
  const base64DecodedId = atob(reportTemplateIdParam || "");
  const reportTemplateId = base64DecodedId.split("ReportTemplateType:")?.pop();

  const reportTemplateQuery = useReportTemplateQuery(
    {
      reportTemplateId: reportTemplateIdParam || "",
    },
    {
      queryKey: ["get-report-template-by-id", reportTemplateId, pathname],
      retry: false,
      refetchOnWindowFocus: false,
      enabled: !!reportTemplateId,
    },
  );

  useEffect(() => {
    const template = reportTemplateQuery.data?.getReportTemplate;

    if (template) {
      setReportTemplate(template as ReportTemplateType);
    }
  }, [reportTemplateQuery.data]);

  const reportTemplateGlobalsQuery = useReportTemplateGlobalsQuery(
    {
      reportTemplateId: reportTemplateIdParam || "",
    },
    {
      queryKey: ["get-report-template-globals", reportTemplateId, pathname],
      retry: false,
      refetchOnWindowFocus: false,
      enabled: !!reportTemplateId,
    },
  );

  const sampleStatusQuery = useReportTemplateSampleStatusQuery(
    {
      reportTemplateId: reportTemplateIdParam || "",
    },
    {
      queryKey: ["get-report-template-sample-status", reportTemplateId, pathname],
      enabled: !!reportTemplateId,
      refetchInterval: (query) => {
        if (query.state.data?.getReportTemplate?.sampleStatus === ApiReportTemplateSampleStatusChoices.Generating) {
          return config.REPORT_POLLING_INTERVAL;
        }
        return false;
      },
    },
  );

  const globalFields = reportTemplateGlobalsQuery.data?.getReportTemplateGlobals?.edges;
  const globalFieldNodes = globalFields?.map((edge) => edge?.node) || [];

  const isReadonly = reportTemplate?.status !== ApiReportTemplateStatusChoices.Draft;

  useEffect(() => {
    if (reportTemplate?.reportTemplateSections?.edges && globalFieldNodes) {
      const adopted = adoptedSectionsRef.current;
      // Only a copy with no unsaved edits takes the rebuilt sections (late globals, a logo refetch);
      // handleSave reconciles its own refetch, including edits made while it was in flight. Deleting
      // every section is an unsaved edit too, so an empty copy is not taken for one not loaded yet.
      if (isSavingRef.current || (adopted !== null && sectionsRef.current !== adopted)) return;

      const sections = buildTemplateSections(reportTemplate, globalFieldNodes);
      adoptedSectionsRef.current = sections;
      replaceSections(sections);
    }
  }, [reportTemplate?.reportTemplateSections?.edges, globalFieldNodes?.length]);

  const updateReportTemplate = useUpdateReportTemplateMutation();
  const createSampleTemplate = useCreateSampleTemplateMutation();

  const handleSave = async () => {
    if (!reportTemplate) {
      notifyError("Could not save template: invalid template!");
      return;
    }
    if (reloadRequired) {
      notifyError(RELOAD_AFTER_SAVE_MESSAGE);
      return;
    }
    // Held until the refetch is reconciled: a second save before then would resend temporary ids.
    if (isSavingRef.current) return;

    const sent = toSectionInputs(sectionsRef.current);
    markSaving(true);
    try {
      const response = await updateReportTemplate.mutateAsync({
        input: buildSaveInput(reportTemplate, sent, treatmentPlanTemplateState),
      });
      const data = response.updateReportTemplate;
      if (!data?.status) {
        notifyError(data?.message || "Could not save report template.");
        return;
      }
      setTreatmentPlanTemplateState(null);
      await reconcileSave(sent, data.message);
    } catch (error) {
      console.error(error);
      notifyError("Could not save report template.");
    } finally {
      markSaving(false);
    }
  };

  // Matches the editor to what the save stored. Only a temporary id the refetch can't pair locks the
  // editor, since a later save would create that row again.
  const reconcileSave = async (sent: ReportTemplateSectionInput[], message: string | null | undefined) => {
    const refetched = await refetchAfterSave(sent);
    await sampleStatusQuery.refetch();
    const savedIds = refetched?.savedIds ?? (sendsTemporaryIds(sent) ? null : NO_SAVED_IDS);
    if (!savedIds) {
      setReloadRequired(true);
      notifyError(RELOAD_AFTER_SAVE_MESSAGE);
      return;
    }
    notifySuccess(message || "Successfully saved report template");

    if (refetched) {
      takeServerCopy(refetched.serverSections, savedIds, sent);
    } else if (!editedSince(sent)) {
      // Both refetches failed, and the current copy is what was saved.
      adoptedSectionsRef.current = sectionsRef.current;
      markSaved();
    }
  };

  // A second try covers a refetch that failed, or that joined a request already in flight before the
  // save. Returns the last copy read and its saved ids (null when it doesn't hold the save).
  const refetchAfterSave = async (sent: ReportTemplateSectionInput[]) => {
    let latest: { serverSections: ReportTemplateSection[]; savedIds: SavedIds | null } | null = null;
    for (let attempt = 0; attempt < 2 && !latest?.savedIds; attempt++) {
      const result = await reportTemplateQuery.refetch();
      // A failed refetch still returns the cached pre-save data.
      const savedTemplate = result.isError
        ? undefined
        : (result.data?.getReportTemplate as ReportTemplateType | undefined);
      if (savedTemplate) {
        const serverSections = buildTemplateSections(savedTemplate, globalFieldNodes);
        latest = { serverSections, savedIds: matchSavedIds(sent, serverSections) };
      }
    }
    return latest;
  };

  // Compares what the next save would send, so an update that changed nothing isn't an edit.
  const editedSince = (sent: ReportTemplateSectionInput[]) => !isEqual(toSectionInputs(sectionsRef.current), sent);

  const markSaved = () => {
    setChanged(false);
    setTimeout(() => {
      setSampleInvalidated(false);
    }, 1000);
  };

  // Takes the refetched copy, or carries edits made during the save onto it.
  const takeServerCopy = (
    serverSections: ReportTemplateSection[],
    savedIds: SavedIds,
    sent: ReportTemplateSectionInput[],
  ) => {
    if (editedSince(sent)) {
      replaceSections(rebaseOnServer(sectionsRef.current, savedIds, serverSections));
    } else {
      adoptedSectionsRef.current = serverSections;
      replaceSections(serverSections);
      markSaved();
    }
    const nextItemKeys = { ...itemKeysRef.current };
    for (const [tempId, savedId] of [...savedIds.sections, ...savedIds.fields]) {
      nextItemKeys[savedId] = itemKeysRef.current[tempId] ?? tempId;
    }
    replaceItemKeys(nextItemKeys);
  };

  const handleCreateSampleExport = async () => {
    if (!reportTemplate) {
      notifyError("Could not export template: invalid template!");
      return;
    }

    await createSampleTemplate.mutateAsync(
      {
        reportTemplate: reportTemplate.id || "",
      },
      {
        onSuccess: async (response) => {
          if (!response.createSampleTemplate?.status) {
            notifyError(response.createSampleTemplate?.message || "Could not save report template.");
            return;
          }

          notifySuccess(response.createSampleTemplate?.message || "Successfully saved report template");
          sampleStatusQuery.refetch();
        },
        onError: async (error: unknown) => {
          console.error("Error when saving report template", error);
          notifyError("Could not save report template.");
        },
      },
    );
  };

  const handleDownloadSampleExport = async () => {
    if (!sampleStatusQuery.data?.getReportTemplate?.sampleFileSignedUrl) {
      notifyError("Could not export template: invalid url!");
      return;
    }

    downloadSampleTemplatePdfFile(sampleStatusQuery.data.getReportTemplate.sampleFileSignedUrl);
  };

  if (reportTemplateQuery.isLoading || !reportTemplate) {
    return <SkeletonLoader loading={true} />;
  }

  return (
    <TemplateEditorProvider>
      <div className="flex w-full flex-col">
        <div className="grid grid-cols-4 gap-x-1 pb-12">
          <div id={"TemplateEditor"} className={`col-span-3 flex w-full flex-col gap-y-2`}>
            <TemplateHeader
              readonly={isReadonly}
              reportTemplate={reportTemplate}
              setReportTemplate={setReportTemplate}
              onSaveTitle={handleSaveTitle}
            />
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-4">
                <TemplateSettings
                  readonly={isReadonly}
                  reportTemplate={reportTemplate}
                  reportTemplateRefetch={() => {
                    reportTemplateQuery.refetch();
                  }}
                  reportSampleStatusRefetch={() => sampleStatusQuery.refetch()}
                  activeTab={activeTab}
                >
                  <PopoverPanel direction="bottom-right" width="w-[500px]" height="max-h-[610px]" content={<div></div>}>
                    <Button
                      variant="secondary"
                      buttonStyle="border"
                      text={
                        <div className="flex items-center">
                          <Cog6ToothIcon className="mr-1 h-4 w-4" />
                          <Small>Template Settings</Small>
                        </div>
                      }
                      className="right"
                    />
                  </PopoverPanel>
                </TemplateSettings>
              </div>

              {(reportTemplate.reportType === ApiReportTemplateReportTypeChoices.InitialAssessment ||
                reportTemplate.reportType === ApiReportTemplateReportTypeChoices.Reassessment) &&
                reportTemplate.treatmentPlanTemplate && (
                  <SegmentedTabs<TemplateEditorTab>
                    tabs={[
                      { value: TEMPLATE_EDITOR_TABS.NARRATIVE, label: "Narrative" },
                      { value: TEMPLATE_EDITOR_TABS.PROGRAMMING, label: "Programming" },
                    ]}
                    activeTab={activeTab}
                    onChange={setActiveTab}
                  />
                )}

              <div className="flex space-x-2">
                <Button
                  onClick={() => setCollapseFields({ value: true })}
                  variant="secondary"
                  buttonStyle="filled"
                  text={
                    <div className="flex items-center">
                      <ArrowsPointingInIcon className="mr-1 h-4 text-rio-700 w-4" />
                      <Small colorType="primary" className="text-rio-700">
                        Contract All
                      </Small>
                    </div>
                  }
                  appearance="link"
                  className="ml-2 text-right"
                />
                <Button
                  onClick={() => setCollapseFields({ value: false })}
                  variant="secondary"
                  buttonStyle="filled"
                  text={
                    <div className="flex  items-center">
                      <ArrowsPointingOutIcon className="mr-1 text-rio-700 h-4 w-4" />
                      <Small colorType="primary" className="text-rio-700">
                        Expand All
                      </Small>
                    </div>
                  }
                />
              </div>
            </div>
            <TemplateStructure
              isCollapsed={collapseFields}
              reportTemplate={reportTemplate}
              reportTemplateSections={reportTemplateSections}
              setReportTemplateSections={replaceSections}
              getLatestSections={getLatestSections}
              resolveItemId={resolveSectionItemId}
              itemKeys={itemKeys}
              // The Programming tab re-copies its data from the refetch, so an edit made there during a
              // save would be dropped or resent as new; the sections tab keeps edits made meanwhile.
              readonly={isReadonly || reloadRequired || (isSaving && activeTab === TEMPLATE_EDITOR_TABS.PROGRAMMING)}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              onChange={() => {
                setChanged(true);
                setSampleInvalidated(true);
              }}
              onTreatmentPlanTemplateUpdate={setTreatmentPlanTemplateState}
            />
          </div>
          <div className="flex w-full flex-col pl-8 gap-2">
            <nav className="sticky top-[70px] flex flex-col items-start">
              <div
                className={`flex flex-col items-start ${isReadonly ? "max-h-[calc(100vh-130px)]" : "max-h-[calc(100vh-194px)]"} overflow-y-scroll gap-2 w-full`}
              >
                <TemplateExportActionPanel
                  onExport={handleCreateSampleExport}
                  onDownload={handleDownloadSampleExport}
                  sampleStatus={
                    sampleStatusQuery.data?.getReportTemplate
                      ?.sampleStatus as unknown as ReportTemplateSampleStatusEnums
                  }
                  disableSampleButton={changed}
                  sampleInvalidated={sampleInvalidated}
                />
                {isUserEnterpriseAdmin && <TemplateExportJson templateId={reportTemplate.id} />}
                {reportTemplateSections.length > 0 && (
                  <StickyNavCollapsible
                    key={reportTemplateSections.map((s) => itemKeys[s.id] ?? s.id).join("-")}
                    sections={reportTemplateSections}
                    reportType={reportTemplate.reportType}
                  />
                )}
              </div>
            </nav>
          </div>
        </div>

        <TemplateEditorFooterButtonRow
          template={reportTemplate}
          onSave={handleSave}
          refetch={() => {
            reportTemplateQuery.refetch();
            sampleStatusQuery.refetch();
          }}
          disablePublishButton={changed}
          isSaving={isSaving || updateReportTemplate.isPending}
        />
      </div>
    </TemplateEditorProvider>
  );
};
